<script lang="ts">
	//Services
	import { format } from '../../../services/i18n';
	import {
		canUpdateFW,
		checkDeviceForceUpdate,
		checkTRVForceUpdateWithBootloader,
		updateFW,
	} from '../../../helpers/firmware_helpers';
	//Design system components
	import { Button, Container, Modal, Text, Alert } from '@Shelly/shelly-design-system';
	//Helpers
	import { classMap } from '@Shelly/shelly-design-system/helpers/component-utils';
	import { setSidePanelState } from 'src/components/Sidepanel/sidepanel-helpers';
	//Types
	import type { Device } from '../../../types/Device';

	export let device: Device,
		target_fw = 'stable';

	export let hasMarginBottomM: boolean;
	export let hasMarginBottomS: boolean;
	export let isOffline = false;

	let showModal = false,
		waitForResponse = false;

	const update = async () => {
		waitForResponse = true;
		const res = await updateFW(device, target_fw);
		if (res.isok) {
			setSidePanelState({ open: true }); //Pako-adding this drawer state(update) because app freezes
		}
		waitForResponse = false;
		showModal = false;
	};

	const openModal = () => {
		showModal = true;
	};
</script>

{#if showModal}
	<Modal
		{showModal}
		on:close={() => (showModal = false)}
		title={`${$format('title_update_firmware_update', {
			default: 'Update Firmware',
		})}?`}
	>
		<svelte:fragment slot="content">
			{#if !canUpdateFW(device)}
				<Alert
					class="mb-l"
					type="warning"
					heading={$format('charge_battery_update_msg', {
						default: 'Please charge your battery <b>above 20%</b> to enable device update.',
					})}
				/>
			{/if}
			<Container padding="0">
				{#if canUpdateFW(device)}
					{#if checkDeviceForceUpdate(device)}
						<Alert
							heading={$format('update_firmware_simple_info', {
								default:
									'Updating the firmware will install the latest software version on your device, providing new features, improvements, and bug fixes.',
							})}
							message={checkTRVForceUpdateWithBootloader(device) &&
								$format('firmware_attention_blu_trv', {
									default: `The first update may require a bootloader update, which will need to run through two update cycles and may take up to 2-5 minutes.`,
								})}
						/>
					{:else}
						<Text>
							{$format('update_firmware_info_prompt', {
								default:
									'Updating the firmware will install the latest software version on your device, providing new features, improvements, and bug fixes. Please be aware that during or after the update, your device may change its status or send invalid values. We advise against updating if you do not have physical access to the device or the devices it controls.',
							})}
						</Text>
					{/if}
				{:else}
					<Text>
						{$format('update_firmware_info_prompt', {
							default:
								'Updating the firmware will install the latest software version on your device, providing new features, improvements, and bug fixes. Please be aware that during or after the update, your device may change its status or send invalid values. We advise against updating if you do not have physical access to the device or the devices it controls.',
						})}
					</Text>
				{/if}
			</Container>
		</svelte:fragment>
		<div class="d-flex justify-content-end" slot="footer">
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s text-capitalize"
				disabled={isOffline}
				responsiveSize={{
					sm: 'm',
				}}
				variant="secondary"
				on:click={() => (showModal = false)}
			>
				{$format('button_cancel', { default: 'Cancel' })}
			</Button>
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
				responsiveSize={{
					sm: 'm',
				}}
				disabled={!canUpdateFW(device) || isOffline}
				loading={waitForResponse}
				on:click={update}
			>
				{$format('sub_settings_title_update_firmware', {
					default: 'Update firmware',
				})}
			</Button>
		</div>
	</Modal>
{/if}

<slot {waitForResponse} {openModal}>
	<Button
		size="m"
		disabled={isOffline}
		class={classMap({
			'align-self-center text-capitalize': true,
			'mb-m': hasMarginBottomM,
			'mb-s': hasMarginBottomS,
		})}
		width="full"
		variant="primary"
		loading={waitForResponse}
		on:click={openModal}>{$format('button_update', { default: 'update' })}</Button
	>
</slot>
